import { ArrowLeftRight, Laptop, RefreshCw, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Logo } from '../components/Layout';
import { Alert, Button, Field, Input } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth, useAuthOptions } from '../lib/auth';
import { openSchool } from '../lib/school';

const FEATURES = [
  { icon: RefreshCw, title: 'SIS roster sync', text: 'Students, faculty, staff, alumni, and candidates stay current automatically.' },
  { icon: Laptop, title: 'Every device', text: 'Chromebooks from Google Admin, plus Macs, iPads, and Windows PCs from Jamf, Mosyle, and Intune.' },
  { icon: ArrowLeftRight, title: 'Fast check in / out', text: 'Scan a tag and assign devices to people, rooms, or carts in seconds.' },
  { icon: ShieldCheck, title: 'Private to your school', text: 'Every school gets its own isolated environment, sign-in page, and audit trail.' },
];

const DEMO_ACCOUNTS = [
  { email: 'admin@edvora.local', label: 'Edvora platform admin' },
  { email: 'it.director@lincoln-academy.example.org', label: 'Lincoln Academy · Admin' },
  { email: 'tech@lincoln-academy.example.org', label: 'Lincoln Academy · Technician' },
  { email: 'principal@lincoln-academy.example.org', label: 'Lincoln Academy · Viewer' },
  { email: 'teacher@lincoln-academy.example.org', label: 'Lincoln Academy · Requester (teacher)' },
  { email: 'admin@riverside-prep.example.org', label: 'Riverside Prep · Admin' },
];

/** Edvora-wide sign-in at /login: finds a school's sign-in page, or signs in Edvora staff with a password. */
export function LoginPage() {
  const { me, login } = useAuth();
  const [params] = useSearchParams();
  const [school, setSchool] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(params.get('error') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const options = useAuthOptions();
  const signUp = useQuery({ queryKey: ['sign-up'], queryFn: () => api.get<{ enabled: boolean; trialDays?: number }>('/sign-up'), staleTime: 60_000 });

  // Already signed in: go to the school this session last used, or the platform.
  useEffect(() => {
    if (!me) return;
    const slug = me.tenant?.slug ?? me.memberships[0]?.slug;
    if (slug) openSchool(slug);
    else if (me.user.isPlatformAdmin) window.location.assign('/platform');
  }, [me]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await login(email, password);
      if (result.school) openSchool(result.school);
      else window.location.assign('/platform');
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  };

  const findSchool = (event: FormEvent) => {
    event.preventDefault();
    const slug = school.trim().toLowerCase().replace(/^\/+|\/+$/g, '').split('/').pop() ?? '';
    if (slug) openSchool(slug, '/login');
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-brand-950 lg:block">
        <div className="absolute -top-40 -right-40 size-[32rem] rounded-full bg-brand-800/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 size-96 rounded-full bg-brand-600/20 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo className="size-10" />
            <div className="leading-tight">
              <p className="font-semibold">Edvora</p>
              <p className="text-xs font-medium tracking-wider text-brand-300 uppercase">Campus Asset</p>
            </div>
          </div>
          <div className="max-w-lg">
            <h2 className="text-4xl leading-tight font-semibold tracking-tight">Every device on campus, accounted for.</h2>
            <p className="mt-4 text-brand-200">
              Technology asset management built for independent schools - connected to the systems you already run.
            </p>
            <ul className="mt-10 grid gap-6 sm:grid-cols-2">
              {FEATURES.map((feature) => (
                <li key={feature.title} className="flex gap-3">
                  <div className="rounded-lg bg-white/10 p-2 text-brand-200">
                    <feature.icon className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{feature.title}</p>
                    <p className="mt-0.5 text-sm text-brand-200/80">{feature.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-brand-300/70">© {new Date().getFullYear()} Edvora</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <Logo />
            <span className="font-semibold">Edvora Campus Asset</span>
          </div>
          {error && (
            <Alert tone="red" className="mb-6">
              {error}
            </Alert>
          )}

          <h1 className="text-2xl font-semibold tracking-tight">Find your school</h1>
          <p className="mt-1 text-sm text-slate-500">Students and staff sign in on their school’s own page.</p>
          <form onSubmit={findSchool} className="mt-6 flex gap-2">
            <div className="flex flex-1 items-center rounded-lg border border-slate-300 bg-white pl-3 text-sm focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
              <span className="text-slate-400">{window.location.host}/</span>
              <input
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                placeholder="your-school"
                aria-label="School address"
                className="min-w-0 flex-1 bg-transparent py-2 pr-3 outline-none"
              />
            </div>
            <Button type="submit" variant="primary" disabled={!school.trim()}>
              Go
            </Button>
          </form>

          {signUp.data?.enabled && (
            <p className="mt-4 text-sm text-slate-600">
              New to Edvora?{' '}
              <Link to="/sign-up" className="font-medium text-brand-700 hover:underline">
                Start a free {signUp.data.trialDays ?? 15}-day trial
              </Link>
            </p>
          )}

          <div className="my-10 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> Edvora staff and password accounts <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <Field label="Email">
              <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field
              label="Password"
              hint={
                options.data?.passwordReset && (
                  <Link to="/reset-password" className="font-medium text-brand-700 hover:underline">
                    Forgot your password?
                  </Link>
                )
              }
            >
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Button type="submit" variant="secondary" className="w-full" loading={submitting}>
              Sign in
            </Button>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-10 rounded-lg border border-dashed border-slate-300 p-4">
              <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Local demo accounts</p>
              <p className="mt-1 text-xs text-slate-500">
                Created by <code className="rounded bg-slate-100 px-1">npm run db:seed</code>. Password: <code className="rounded bg-slate-100 px-1">ChangeMe!2026</code>
              </p>
              <ul className="mt-3 space-y-1">
                {DEMO_ACCOUNTS.map((account) => (
                  <li key={account.email}>
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(account.email);
                        setPassword('ChangeMe!2026');
                      }}
                      className="w-full rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-100"
                    >
                      <span className="block font-medium text-slate-800">{account.label}</span>
                      <span className="text-slate-500">{account.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
